import React, { useState, useRef, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import emailjs from 'emailjs-com';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SplitType from 'split-type';
import './Contact.css';

gsap.registerPlugin(ScrollTrigger);

const SERVICE  = process.env.REACT_APP_SERVICE_ID;
const TEMPLATE = process.env.REACT_APP_TEMPLATE_ID;
const USER     = process.env.REACT_APP_USER_ID;

/**
 * Act four. Layout family: centred closing statement.
 *
 * Centring is the exception the rest of the page does not get. It is used
 * here because the closing ask is the message, and because every other act
 * is deliberately off-axis. Arriving at symmetry reads as an ending.
 */
function Contact({ data, personal, standalone = false }) {
  const { email, phone, location } = personal;
  const rootRef = useRef(null);
  const headRef = useRef(null);
  const formRef = useRef(null);

  const [status, setStatus] = useState({ type: 'idle', msg: '' });
  const busy = status.type === 'busy';

  useEffect(() => {
    const ctx = gsap.context(() => {
      const mm = gsap.matchMedia();

      mm.add('(prefers-reduced-motion: no-preference)', () => {
        const split = new SplitType(headRef.current, { types: 'lines' });

        const tweens = [
          gsap.from(split.lines, {
            scrollTrigger: { trigger: headRef.current, start: 'top 84%' },
            yPercent: 106,
            duration: 1.2,
            stagger: 0.1,
            ease: 'power4.out',
          }),
          gsap.from('.contact__field', {
            scrollTrigger: { trigger: '.contact__form', start: 'top 86%' },
            opacity: 0,
            y: 20,
            duration: 0.8,
            stagger: 0.08,
            ease: 'power3.out',
          }),
        ];

        return () => {
          tweens.forEach((t) => t.kill());
          split.revert();
        };
      });
    }, rootRef);

    return () => ctx.revert();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!SERVICE || !TEMPLATE || !USER) {
      setStatus({
        type: 'err',
        msg: `The form is not configured yet. Email me directly at ${email}.`,
      });
      return;
    }

    setStatus({ type: 'busy', msg: '' });

    emailjs
      .sendForm(SERVICE, TEMPLATE, formRef.current, USER)
      .then(() => {
        setStatus({ type: 'ok', msg: 'Message sent. I will come back to you shortly.' });
        formRef.current.reset();
      })
      .catch(() => {
        setStatus({
          type: 'err',
          msg: `That did not send. Try again, or email me at ${email}.`,
        });
      });
  };

  return (
    <>
      {standalone && (
        <Helmet>
          <title>Contact, Damilare Oyedeji</title>
          <meta
            name="description"
            content="Get in touch about engineering work, product builds and design systems."
          />
        </Helmet>
      )}

      <section className="contact act" id="contact" ref={rootRef}>
        <div className="shell contact__inner">

          <div className="contact__left">
            <h2 className="contact__head" ref={headRef}>{data.headline}</h2>
            <p className="contact__lead">{data.message}</p>

            <dl className="contact__direct">
              <div>
                <dt>Email</dt>
                <dd><a href={`mailto:${email}`}>{email}</a></dd>
              </div>
              <div>
                <dt>Phone</dt>
                <dd><a href={`tel:${phone.replace(/\s/g, '')}`}>{phone}</a></dd>
              </div>
              <div>
                <dt>Based in</dt>
                <dd>{location}</dd>
              </div>
            </dl>
          </div>

          <form ref={formRef} onSubmit={handleSubmit} className="contact__form" noValidate={false}>
            <div className="contact__field">
              <label htmlFor="cf-name">Name</label>
              <input id="cf-name" name="name" type="text" required autoComplete="name" placeholder="Your name" />
            </div>

            <div className="contact__field">
              <label htmlFor="cf-email">Email</label>
              <input id="cf-email" name="email" type="email" required autoComplete="email" placeholder="you@company.com" />
            </div>

            <div className="contact__field contact__field--wide">
              <label htmlFor="cf-message">Message</label>
              <textarea id="cf-message" name="message" rows="5" required placeholder="What are you building?"></textarea>
            </div>

            {status.msg && (
              <p
                className={`contact__status contact__status--${status.type}`}
                role={status.type === 'err' ? 'alert' : 'status'}
              >
                {status.msg}
              </p>
            )}

            <button type="submit" className="btn contact__submit" disabled={busy}>
              {busy ? 'Sending' : data.cta}
            </button>
          </form>

        </div>
      </section>
    </>
  );
}

export default Contact;
